/* Stock labels describe the selected variant now, not when a cart was opened. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else root.StoryStock = factory(root);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (env) {
  'use strict';
  const PREORDER = 'Yes — we will confirm before fulfilling';
  const CHECK_ERROR = 'We could not check stock. Please try again before checking out.';
  let pending = Promise.resolve();
  const route = path => ((env.Shopify && env.Shopify.routes && env.Shopify.routes.root) || '/') + path;
  function queue(work) {
    const result = pending.then(work);
    pending = result.catch(() => {});
    return result;
  }
  async function request(url, body) {
    const options = { cache: 'no-store', headers: {},
      signal: AbortSignal.timeout(8000) };
    if (body) {
      options.method = 'POST';
      options.headers['Content-Type'] = 'application/json';
      options.body = JSON.stringify(body);
    }
    let response, data;
    try { response = await env.fetch(url, options); data = await response.json(); }
    catch (_) { throw new Error(CHECK_ERROR); }
    if (!response.ok) throw new Error(data.description || CHECK_ERROR);
    return data;
  }
  async function read(handle, variantId) {
    if (!handle || !variantId) throw new Error(CHECK_ERROR);
    const stock = await request(route('products/' + encodeURIComponent(handle)) +
      '?view=stock&variant=' + encodeURIComponent(variantId) + '&_stock=' + Date.now());
    if (String(stock.variant_id) !== String(variantId) || typeof stock.available !== 'boolean' ||
        !Object.prototype.hasOwnProperty.call(stock, 'inventory_management') ||
        !['continue', 'deny'].includes(stock.inventory_policy) ||
        (stock.inventory_management !== null && !Number.isFinite(stock.inventory_quantity))) {
      throw new Error(CHECK_ERROR);
    }
    return stock;
  }
  function classify(stock, quantity) {
    if (!stock.available) return 'unavailable';
    if (stock.inventory_management === null) return 'in_stock';
    if (stock.inventory_quantity >= quantity) return 'in_stock';
    return stock.inventory_policy === 'continue' ? 'preorder' : 'unavailable';
  }
  function propertiesFor(properties, state) {
    const result = Object.assign({}, properties || {});
    if (state === 'preorder') result['Pre-Order'] = PREORDER;
    else delete result['Pre-Order'];
    // Shopify cannot replace properties with {}. A private stable property
    // removes an old public label without removing/re-adding the customer's book.
    result._stock_status = state;
    return result;
  }
  function labelIsPreorder(item) {
    const value = String((item.properties || {})['Pre-Order'] || '').trim().toLowerCase();
    return value !== '' && value !== 'no' && value !== 'false';
  }
  const trackedItem = item => item.product_type !== 'Gift';
  function annotate(cart, stocks) {
    const totals = new Map();
    for (const item of cart.items) totals.set(String(item.variant_id),
      (totals.get(String(item.variant_id)) || 0) + item.quantity);
    for (const item of cart.items) {
      const id = String(item.variant_id);
      item._stockState = trackedItem(item) ? classify(stocks.get(id), totals.get(id)) : 'in_stock';
    }
    return cart;
  }
  async function inspect() {
    const cart = await request(route('cart.js'));
    const unique = new Map(cart.items.filter(trackedItem).map(item => [String(item.variant_id), item]));
    const stocks = new Map(await Promise.all(Array.from(unique, async ([id, item]) =>
      [id, await read(item.handle, id)])));
    return { cart: annotate(cart, stocks), stocks };
  }
  async function add(options) {
    return queue(async () => {
      const quantity = Number(options.quantity || 1);
      if (!Number.isInteger(quantity) || quantity < 1) throw new Error('Please choose a valid quantity.');
      const [stock, cart] = await Promise.all([read(options.handle, options.id), request(route('cart.js'))]);
      const already = cart.items.filter(item => String(item.variant_id) === String(options.id))
        .reduce((total, item) => total + item.quantity, 0);
      const state = classify(stock, quantity + already);
      if (state === 'unavailable') throw new Error('This quantity is currently unavailable. Please choose fewer copies.');
      if (state === 'preorder') {
        const confirm = options.confirmPreorder || (() => env.confirm(
          'There are not enough copies in stock for this quantity. Add as a pre-order? We will confirm before fulfilling.'));
        if (!(await confirm())) return null;
      }
      return request(route('cart/add.js'), { id: options.id, quantity,
        properties: propertiesFor(options.properties, state) });
    });
  }
  function change(key, quantity) {
    return queue(() => request(route('cart/change.js'), { id: key, quantity }));
  }
  function prepareCheckout(confirmPreorders) {
    return queue(async () => {
      let { cart, stocks } = await inspect();
      const unavailable = cart.items.filter(item => item._stockState === 'unavailable');
      if (unavailable.length) throw new Error('Please update your cart: ' +
        unavailable.map(item => item.product_title).join(', ') + ' is unavailable in this quantity.');
      const changed = cart.items.filter(item => item._stockState === 'preorder' && !labelIsPreorder(item));
      if (changed.length) {
        const confirm = confirmPreorders || (items => env.confirm(
          'Stock has changed for: ' + items.map(item => item.product_title).join(', ') +
          '. Continue with these as pre-orders? We will confirm before fulfilling.'));
        if (!(await confirm(changed))) return null;
      }
      // Properties change line keys and may merge two lines of the same variant.
      // Always select the next line from Shopify's latest returned cart.
      const limit = cart.items.length + 1;
      for (let count = 0; count < limit; count++) {
        const item = cart.items.find(item => trackedItem(item) &&
          ((item.properties || {})._stock_status !== item._stockState ||
           labelIsPreorder(item) !== (item._stockState === 'preorder') ||
           (item._stockState === 'in_stock' && Object.hasOwn(item.properties || {}, 'Pre-Order'))));
        if (!item) return cart;
        cart = annotate(await request(route('cart/change.js'), { id: item.key, quantity: item.quantity,
          properties: propertiesFor(item.properties, item._stockState) }), stocks);
      }
      throw new Error(CHECK_ERROR);
    });
  }
  return { read, classify, add, change, prepareCheckout, propertiesFor, labelIsPreorder,
    inspectCart: () => queue(async () => (await inspect()).cart) };
});
